import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndProvider,
  Tree,
  MultiBackend,
  getBackendOptions,
} from "@minoru/react-dnd-treeview";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import { RiDragMove2Line, RiAddLine, RiDeleteBinLine } from "react-icons/ri";
import { MdToggleOn, MdToggleOff } from "react-icons/md";
import { FaLayerGroup, FaEdit } from "react-icons/fa";

const ROOT_ID = "__root__";

function itemsToNodes(items, allowNesting, parentId = ROOT_ID, path = "root") {
  const nodes = [];
  items.forEach((item, index) => {
    const baseKey = (
      item.id ||
      item.key ||
      item.label ||
      `item-${index}`
    ).toString();
    const nodeId = `${path}/${baseKey}`;
    const { children = [], ...rest } = item || {};
    nodes.push({
      id: nodeId,
      parent: parentId,
      text: item.label || item.key || "Menu",
      droppable: allowNesting, // Vik (Jan 15, 2026) : Control droppable based on nesting toggle
      data: {
        item: { ...rest },
        keyLower: String(item.key || item.label || baseKey).toLowerCase(),
        badge: item.badge,
        disabled: rest.disabled,
      },
    });
    if (children && Array.isArray(children) && children.length > 0) {
      nodes.push(...itemsToNodes(children, allowNesting, nodeId, nodeId));
    }
  });
  return nodes;
}

function nodesToItems(nodes) {
  const map = new Map();
  nodes.forEach((node) => {
    const base = { ...node.data.item, children: [] };
    map.set(node.id, base);
  });

  const rootItems = [];
  nodes.forEach((node) => {
    const item = map.get(node.id);
    if (!item) return;
    const parent = map.get(node.parent);
    if (parent) {
      parent.children.push(item);
    } else if (node.parent === ROOT_ID || !parent) {
      rootItems.push(item);
    }
  });

  const assignOrder = (list) => {
    list.forEach((item, idx) => {
      item.order = idx;
      if (Array.isArray(item.children) && item.children.length > 0) {
        assignOrder(item.children);
      }
    });
  };
  assignOrder(rootItems);

  return rootItems;
}

function collectDescendantIds(treeData, targetId) {
  const ids = [targetId];
  treeData.forEach((node) => {
    if (node.parent === targetId) {
      ids.push(...collectDescendantIds(treeData, node.id));
    }
  });
  return ids;
}

export default function MenuTreeEditor({
  items,
  onChange,
  onRequestAddChild,
  onEditItem,
}) {
  const [allowNesting, setAllowNesting] = useState(false);

  /* Vik (Jan 15, 2026) : Re-generate tree nodes when items OR allowNesting changes */
  const [treeData, setTreeData] = useState(() => itemsToNodes(items, false));

  useEffect(() => {
    setTreeData(itemsToNodes(items, allowNesting));
  }, [items, allowNesting]);

  const handleChange = useCallback(
    (newTree) => {
      setTreeData(newTree);
      if (typeof onChange === "function") {
        onChange(nodesToItems(newTree));
      }
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (newTree) => {
      handleChange(newTree);
    },
    [handleChange],
  );

  const handleDelete = useCallback(
    (nodeId) => {
      if (!window.confirm("Are you sure you want to delete this item?")) {
        return;
      }
      const idsToRemove = collectDescendantIds(treeData, nodeId);
      const filtered = treeData.filter(
        (node) => !idsToRemove.includes(node.id),
      );
      handleChange(filtered);
    },
    [treeData, handleChange],
  );

  const handleToggleDisabled = useCallback(
    (nodeId) => {
      const updated = treeData.map((node) => {
        if (node.id === nodeId) {
          const currentDisabled = node.data?.item?.disabled || false;
          return {
            ...node,
            data: {
              ...node.data,
              item: {
                ...node.data.item,
                disabled: !currentDisabled ? true : undefined, // Set to true to disable, undefined to enable
              },
            },
          };
        }
        return node;
      });
      handleChange(updated);
    },
    [treeData, handleChange],
  );

  const renderNode = useCallback(
    (node, { depth, isOpen, onToggle, dragHandle }) => {
      const badge = node.data?.item?.badge;
      const disabled = node.data?.item?.disabled;
      return (
        <div
          className={`menu-tree-node ${
            disabled ? "menu-tree-node-disabled" : ""
          }`}
          style={{ paddingLeft: depth * 16 }}
          data-node-id={node.id}
        >
          <span
            className="menu-tree-drag"
            ref={dragHandle}
            title="Drag to reorder"
          >
            <RiDragMove2Line size={16} />
          </span>
          {node.droppable && (
            <button
              type="button"
              className="menu-tree-toggle"
              onClick={() => onToggle(node.id)}
              title={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? <FiChevronDown /> : <FiChevronRight />}
            </button>
          )}
          <span className="menu-tree-label">{node.text}</span>
          {badge && <span className="menu-tree-badge">{badge}</span>}
          {disabled && <span className="menu-tree-disabled">DISABLED</span>}
          <span className="menu-tree-spacer" />
          {typeof onEditItem === "function" && (
            <button
              type="button"
              className="menu-tree-btn"
              onClick={() => onEditItem(node.data.item)}
              title="Edit item"
              style={{ color: "#60a5fa" }}
            >
              <FaEdit size={14} />
            </button>
          )}
          <button
            type="button"
            className="menu-tree-btn"
            onClick={() => handleToggleDisabled(node.id)}
            title={disabled ? "Enable item" : "Disable item"}
            style={{ color: disabled ? "#4ade80" : "#fbbf24" }}
          >
            {disabled ? <MdToggleOff size={20} /> : <MdToggleOn size={20} />}
          </button>
          {typeof onRequestAddChild === "function" && (
            <button
              type="button"
              className="menu-tree-btn"
              onClick={() => onRequestAddChild(node.data.keyLower)}
              title="Add child"
            >
              <RiAddLine />
            </button>
          )}
          <button
            type="button"
            className="menu-tree-btn"
            onClick={() => handleDelete(node.id)}
            title="Delete"
          >
            <RiDeleteBinLine />
          </button>
        </div>
      );
    },
    [handleDelete, handleToggleDisabled, onRequestAddChild, onEditItem],
  );

  const handleFlatten = useCallback(() => {
    if (
      !window.confirm(
        "Are you sure you want to flatten the menu structure? All submenus will become top-level items.",
      )
    ) {
      return;
    }
    const flattened = treeData.map((node) => ({
      ...node,
      parent: ROOT_ID,
    }));
    handleChange(flattened);
  }, [treeData, handleChange]);

  const handleCanDrop = useCallback(
    (tree, { dragSource, dropTargetId }) => {
      /* Vik (Jan 15, 2026) : Only allow drops at root level when nesting is disabled */
      if (!allowNesting && dropTargetId !== ROOT_ID) {
        return false;
      }
      return true;
    },
    [allowNesting],
  );

  const tree = useMemo(
    () => (
      <Tree
        rootId={ROOT_ID}
        tree={treeData}
        render={renderNode}
        onDrop={handleDrop}
        canDrop={handleCanDrop}
        /* Vik (Jan 21, 2026) : sort={false} is REQUIRED for drag-drop reordering to work.
         Without this, the Tree library auto-sorts nodes and onDrop receives the same
         tree order as before, making reordering impossible. */
        sort={false}
        classes={{
          root: "menu-tree",
          draggingSource: "menu-tree-dragging",
          dropTarget: "menu-tree-drop-target",
        }}
      />
    ),
    [treeData, renderNode, handleDrop, handleCanDrop],
  );

  return (
    <div className="menu-tree-container">
      <div
        className="menu-tree-toolbar"
        style={{
          marginBottom: "10px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
        }}
      >
        {/* <button
          type="button"
          className="btn btn-sm btn-outline-warning"
          onClick={handleFlatten}
          title="Move all items to top level"
        >
          <FaLayerGroup style={{ marginRight: "5px" }} />
          Flatten Menu
        </button>
        <div className="form-check form-switch" style={{ margin: 0 }}>
          <input
            className="form-check-input"
            type="checkbox"
            id="allowNestingToggle"
            checked={allowNesting}
            onChange={(e) => setAllowNesting(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="allowNestingToggle" style={{ color: "var(--text-color, #fff)" }}>
            Allow Nesting via Drag & Drop
          </label>
        </div> */}
      </div>
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        {tree}
      </DndProvider>
    </div>
  );
}
